export type TierSlug = 'beginner' | 'casual' | 'active' | 'professional';

/**
 * What a plan allows. Mirrors TIER_LIMITS in backend core/tier_limits.py, which is
 * what's enforced: change both together (pricingTiers.test.ts fails when they
 * differ). The card bullets, the AI highlight, the comparison tables and every
 * page's limits and feature gates are generated from these numbers, so no screen
 * can show a limit of its own.
 */
export interface PlanLimits {
  watchlist: number;
  portfolio: number;
  /** alerts: price and technical alerts count against it together */
  priceAlerts: number;
  /** review_period: DCF valuations, TA, AI and watchlist screens reset per this */
  period: 'week' | 'day';
  dcf: number;
  technicalAnalysis: number;
  /** 0 = not included */
  aiAnalysis: number;
  /** indicator_alerts: a total that can be active at once, not per period; 0 = not included */
  smartAlerts: number;
  /** 0 = not included */
  savedScreens: number;
  /** watchlist_screens, per period; 0 = not included */
  watchlistScreens: number;
  /** Frontend-only: client-side math, so the server has no key for it */
  optionsCalculator: boolean;
}

export interface PricingTier {
  slug: TierSlug;
  name: string;
  shortName: string;
  price: string;
  period: string;
  monthlyPriceNum: number;
  hasTrial: boolean;
  trialBadge?: string;
  popular?: boolean;
  limits: PlanLimits;
  highlightedBullet?: string;
  bullets: string[];
  ctaLabel: string;
  description: string;
}

type TierDefinition = Omit<PricingTier, 'highlightedBullet' | 'bullets'> & {
  /** Card lines that aren't limits, listed after the generated ones */
  extraBullets?: string[];
};

const TIER_DEFINITIONS: TierDefinition[] = [
  {
    slug: 'beginner',
    name: 'Beginner',
    shortName: 'Beginner',
    price: '$10',
    period: '/month',
    monthlyPriceNum: 10,
    hasTrial: true,
    trialBadge: '14-day free trial',
    description: 'Perfect for getting started — 14-day free trial',
    limits: {
      watchlist: 10, portfolio: 10, priceAlerts: 5, period: 'week',
      dcf: 5, technicalAnalysis: 5, aiAnalysis: 0,
      smartAlerts: 0, savedScreens: 0, watchlistScreens: 0, optionsCalculator: false,
    },
    extraBullets: ['Real-time WebSocket prices'],
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'casual',
    name: 'Casual Investor',
    shortName: 'Casual',
    price: '$20',
    period: '/month',
    monthlyPriceNum: 20,
    hasTrial: true,
    trialBadge: '14-day free trial',
    description: 'For investors tracking a moderate portfolio — 14-day free trial',
    limits: {
      watchlist: 20, portfolio: 20, priceAlerts: 10, period: 'week',
      dcf: 15, technicalAnalysis: 15, aiAnalysis: 5,
      smartAlerts: 0, savedScreens: 0, watchlistScreens: 0, optionsCalculator: false,
    },
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'active',
    name: 'Active Investor',
    shortName: 'Active',
    price: '$40',
    period: '/month',
    monthlyPriceNum: 40,
    hasTrial: false,
    popular: true,
    description: 'For active traders with larger portfolios',
    limits: {
      watchlist: 45, portfolio: 45, priceAlerts: 20, period: 'day',
      dcf: 20, technicalAnalysis: 10, aiAnalysis: 10,
      smartAlerts: 5, savedScreens: 15, watchlistScreens: 5, optionsCalculator: true,
    },
    ctaLabel: 'Get Active',
  },
  {
    slug: 'professional',
    name: 'Professional',
    shortName: 'Professional',
    price: '$50',
    period: '/month',
    monthlyPriceNum: 50,
    hasTrial: false,
    description: 'Ultimate tools for professional traders',
    limits: {
      watchlist: 75, portfolio: 75, priceAlerts: 50, period: 'day',
      dcf: 40, technicalAnalysis: 20, aiAnalysis: 25,
      smartAlerts: 20, savedScreens: 50, watchlistScreens: 15, optionsCalculator: true,
    },
    ctaLabel: 'Go Professional',
  },
];

const limitBullets = (l: PlanLimits): string[] => [
  `${l.watchlist} watchlist stocks`,
  `${l.portfolio} portfolio entries`,
  `${l.priceAlerts} price alerts`,
  `${l.dcf} DCF valuations per ${l.period}`,
  `${l.technicalAnalysis} technical analyses per ${l.period}`,
  ...(l.smartAlerts ? [`${l.smartAlerts} smart technical alerts (RSI, MACD, MA cross…)`] : []),
  'Email price alerts',
  ...(l.savedScreens ? ['Save your screens'] : []),
  ...(l.optionsCalculator ? ['Options Calculator'] : []),
];

export const PRICING_TIERS: PricingTier[] = TIER_DEFINITIONS.map(({ extraBullets, ...tier }) => ({
  ...tier,
  highlightedBullet: tier.limits.aiAnalysis
    ? `✦ AI Stock & Portfolio Analysis — ${tier.limits.aiAnalysis}/${tier.limits.period}`
    : undefined,
  bullets: [...limitBullets(tier.limits), ...(extraBullets ?? [])],
}));

/**
 * Whether the public site invites new sign-ups. False while NWC-Analytics is paused
 * (2026-10-31): every public sign-up button and link (Landing, Pricing, Login, the
 * FAQ/Terms/Privacy headers) is hidden. /register and /registerwithpayment still work
 * for anyone who types the URL, and logged-in users keep their plan-change buttons.
 * Set true to reopen.
 */
export const NEW_SIGNUPS_OPEN = false;

export const getTierBySlug = (slug: TierSlug): PricingTier =>
  PRICING_TIERS.find((t) => t.slug === slug) ?? PRICING_TIERS[0];

// ── Tier order and gates ───────────────────────────────────────────────────
// The one frontend tier order, lowest plan first. Upgrade paths, ranks and
// "which plans include X" all read it; no page keeps its own list.

export const TIER_ORDER: TierSlug[] = PRICING_TIERS.map((t) => t.slug);

export const isTierSlug = (value: unknown): value is TierSlug =>
  typeof value === 'string' && TIER_ORDER.indexOf(value as TierSlug) !== -1;

/**
 * A tier from untrusted input (?tier=, a user record still loading). Anything
 * unknown is the lowest plan, as on the backend (DEFAULT_TIER in tier_limits.py).
 */
export const toTierSlug = (value: unknown): TierSlug => (isTierSlug(value) ? value : TIER_ORDER[0]);

/** 0 for the lowest plan, rising with price. */
export const tierRank = (tier: unknown): number => TIER_ORDER.indexOf(toTierSlug(tier));

/** A plan's limits; an unknown tier gets the lowest plan's. */
export const getTierLimits = (tier: unknown): PlanLimits => getTierBySlug(toTierSlug(tier)).limits;

/** The limits that switch a feature on: a count (0 = not included) or a flag. */
export type PlanFeature = Exclude<keyof PlanLimits, 'period'>;

/**
 * Whether a plan includes a feature: the one frontend gate. It reads the
 * feature's own limit, so it moves with the plan data. The server enforces
 * every limit it has a key for; this only decides what a page shows.
 */
export const planIncludes = (tier: unknown, feature: PlanFeature): boolean => {
  const value = getTierLimits(tier)[feature];
  return typeof value === 'boolean' ? value : value > 0;
};

/** The plans that include a feature, lowest first. */
export const tiersWith = (feature: PlanFeature): PricingTier[] =>
  PRICING_TIERS.filter((t) => planIncludes(t.slug, feature));

/** The lowest plan that includes a feature (undefined if none does). */
export const lowestTierWith = (feature: PlanFeature): TierSlug | undefined => tiersWith(feature)[0]?.slug;

/** "A", "A and B", "A, B, and C" */
const joinNames = (names: string[]): string =>
  names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;

/** "Active and Professional": the plans that include a feature, for copy. */
export const planNamesWith = (feature: PlanFeature): string =>
  joinNames(tiersWith(feature).map((t) => t.shortName));

/** "Beginner, Casual, Active, and Professional" */
export const ALL_PLAN_NAMES = joinNames(PRICING_TIERS.map((t) => t.shortName));

/** "Beginner and Casual" — the plans with a free trial (backend: TRIAL_TIERS in stripe_payments.py). */
export const TRIAL_PLAN_NAMES = joinNames(PRICING_TIERS.filter((t) => t.hasTrial).map((t) => t.shortName));

/**
 * Price label and full feature list for compact tier cards (upgrade prompts,
 * account page). Those screens used to keep their own copies, and every copy
 * drifted from the backend's tier_limits.py — this file is the one to update.
 */
export const getTierSummary = (slug: TierSlug): { price: string; features: string[] } => {
  const t = getTierBySlug(slug);
  return {
    price: `${t.price}/mo`,
    features: [
      ...(t.highlightedBullet ? [t.highlightedBullet.replace(/^✦\s*/, '')] : []),
      ...t.bullets,
    ],
  };
};

/** One row of a plan comparison table: true = ✓, false = not included. */
export interface ComparisonRow {
  label: string;
  values: Record<TierSlug, string | boolean>;
}

const comparisonRow = (label: string, value: (t: PricingTier) => string | boolean): ComparisonRow => ({
  label,
  values: Object.fromEntries(PRICING_TIERS.map((t) => [t.slug, value(t)])) as Record<TierSlug, string | boolean>,
});

const perPeriod = (n: number, t: PricingTier) => `${n}/${t.limits.period}`;

/** The plan comparison, for the Pricing page and the Upgrade Required page. */
export const COMPARISON_ROWS: ComparisonRow[] = [
  comparisonRow('Watchlist Stocks', (t) => String(t.limits.watchlist)),
  comparisonRow('Portfolio Entries', (t) => String(t.limits.portfolio)),
  comparisonRow('Price Alerts', (t) => String(t.limits.priceAlerts)),
  comparisonRow('DCF Valuations', (t) => perPeriod(t.limits.dcf, t)),
  comparisonRow('Technical Analysis', (t) => perPeriod(t.limits.technicalAnalysis, t)),
  comparisonRow('AI Stock & Portfolio Analysis', (t) => (t.limits.aiAnalysis ? perPeriod(t.limits.aiAnalysis, t) : false)),
  comparisonRow('Smart Technical Alerts', (t) => (t.limits.smartAlerts ? String(t.limits.smartAlerts) : false)),
  comparisonRow('Saved Screens', (t) => (t.limits.savedScreens ? String(t.limits.savedScreens) : false)),
  comparisonRow('Options Calculator', (t) => t.limits.optionsCalculator),
  comparisonRow('Email Alerts', () => true),
  comparisonRow('14-Day Free Trial', (t) => t.hasTrial),
];
